import axios, { type AxiosError } from 'axios';
import { isSessionEnding } from './sessionState';

/**
 * Shared normalization for the API's error envelope:
 * `{ error_code, error_message, error_details, exception_message }`.
 */

function parseMaybeJsonString(value: unknown): unknown {
  if (typeof value !== 'string') {
    return value;
  }
  const s = value.trim();
  if (!s.startsWith('[') && !s.startsWith('{')) {
    return value;
  }
  try {
    return JSON.parse(s) as unknown;
  } catch {
    return value;
  }
}

export function normalizeErrorBody(data: unknown): {
  errorCode?: string;
  errorMessage?: string;
  errorDetails?: unknown;
  exceptionMessage?: string;
} {
  const raw = parseMaybeJsonString(data) as Record<string, unknown> | null | undefined;
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  const errorCode = (raw.error_code ?? raw.errorCode) as string | undefined;
  const errorMessage = (raw.error_message ?? raw.errorMessage ?? raw.message) as string | undefined;
  const exceptionMessage = (raw.exception_message ?? raw.exceptionMessage) as string | undefined;
  let errorDetails = raw.error_details ?? raw.errorDetails;
  errorDetails = parseMaybeJsonString(errorDetails);
  return { errorCode, errorMessage, errorDetails, exceptionMessage };
}

export function getResponseData(e: unknown): unknown {
  if (axios.isAxiosError(e)) {
    return (e as AxiosError).response?.data;
  }
  const withResponse = e as { response?: { data?: unknown } };
  return withResponse?.response?.data;
}

export interface NormalizedApiError {
  status?: number;
  errorCode?: string;
  errorMessage?: string;
  errorDetails?: unknown;
  /** PHP stack trace. Logged to the console, never rendered in the UI. */
  exceptionMessage?: string;
  isCanceled: boolean;
  isTimeout: boolean;
  isNetworkError: boolean;
}

/** `error_code` values the user must never see a toast for. */
export const SILENT_ERROR_CODES = new Set(['UnauthorizedCode']);

/**
 * Codes that mean "the backend crashed". Their `error_message` is an untranslated
 * English apology, so we drop it and let the i18n fallback speak instead.
 */
export const OPAQUE_ERROR_CODES = new Set(['Internal']);

export function normalizeApiError(e: unknown): NormalizedApiError {
  const isAxios = axios.isAxiosError(e);
  const err = isAxios ? (e as AxiosError) : undefined;
  const data = getResponseData(e);
  const fromBody = normalizeErrorBody(data);

  const { errorCode, errorDetails, exceptionMessage } = fromBody;
  let { errorMessage } = fromBody;

  if (!errorCode && !errorMessage && errorDetails == null) {
    if (typeof data === 'string' && data.trim() !== '') {
      errorMessage = data;
    } else if (err?.message) {
      errorMessage = err.message;
    } else if (e instanceof Error && e.message) {
      errorMessage = e.message;
    }
  }

  const status = err?.response?.status;
  const isTimeout = err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT';

  return {
    status,
    errorCode,
    errorMessage,
    errorDetails,
    exceptionMessage,
    isCanceled: isAxios && (err?.code === 'ERR_CANCELED' || axios.isCancel(e)),
    isTimeout,
    // No response and not an abort/timeout: the request never reached the server.
    isNetworkError: isAxios && !err?.response && !isTimeout && err?.code !== 'ERR_CANCELED',
  };
}

/** Back-compat shape for modules that already destructure these three fields. */
export function readApiError(e: unknown): {
  errorCode?: string;
  errorMessage?: string;
  errorDetails?: unknown;
} {
  const { errorCode, errorMessage, errorDetails } = normalizeApiError(e);
  return { errorCode, errorMessage, errorDetails };
}

export const UNAUTHORIZED_STATUS = 401;

/**
 * Whether the interceptor should raise a toast. Silent cases: `silentError`,
 * a session ending, an aborted request, and 401 / `UnauthorizedCode`.
 */
export function shouldNotifyApiError(normalized: NormalizedApiError, silentError?: boolean): boolean {
  if (silentError) {
    return false;
  }
  // Covers failures with no HTTP response, e.g. killed by the login redirect.
  if (isSessionEnding()) {
    return false;
  }
  if (normalized.isCanceled) {
    return false;
  }
  if (normalized.status === UNAUTHORIZED_STATUS) {
    return false;
  }
  if (normalized.errorCode && SILENT_ERROR_CODES.has(normalized.errorCode)) {
    return false;
  }
  return true;
}
